// v3/shared/stores/location-store.ts — Geolocation permission status store
//
// Hybrid approach:
// - Uses Permissions API listener for real-time revocation detection (Chrome, Edge, Firefox)
// - Falls back to re-checking on demand for Safari (no Permissions API support)
// Does NOT store coordinates — components fetch fresh coordinates when needed.

import { create } from 'zustand';

export type LocationStatus = 'loading' | 'granted' | 'denied';

interface LocationState {
  status: LocationStatus;
  /** Whether the Permissions API listener is active (no need for manual re-checks) */
  hasListener: boolean;
  /** Initial check + set up listener if available */
  init: () => void;
  /** Manual re-check (used as fallback on Safari, or on route change) */
  recheck: () => void;
}

export const useLocationStore = create<LocationState>((set, get) => ({
  status: 'loading',
  hasListener: false,

  init: () => {
    if (!navigator.geolocation) {
      set({ status: 'denied' });
      return;
    }

    // Try Permissions API for real-time listener
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        // Set initial status from permission state
        const mapState = (state: PermissionState): LocationStatus => {
          if (state === 'granted') return 'granted';
          if (state === 'denied') return 'denied';
          return 'loading'; // 'prompt' — need to actually request
        };

        const initial = mapState(permissionStatus.state);

        if (initial === 'loading') {
          // Permission is 'prompt' — need to actually request to trigger the browser dialog
          navigator.geolocation.getCurrentPosition(
            () => set({ status: 'granted' }),
            () => set({ status: 'denied' }),
            { timeout: 10000 },
          );
        } else {
          set({ status: initial });
        }

        // Listen for changes (fires when user revokes/grants in browser settings)
        permissionStatus.addEventListener('change', () => {
          set({ status: mapState(permissionStatus.state) });
        });
        set({ hasListener: true });
      }).catch(() => {
        // Permissions API not supported — fall back to direct request
        get().recheck();
      });
    } else {
      // No Permissions API (Safari) — direct request
      get().recheck();
    }
  },

  recheck: () => {
    if (!navigator.geolocation) {
      set({ status: 'denied' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => set({ status: 'granted' }),
      () => set({ status: 'denied' }),
      { timeout: 10000 },
    );
  },
}));
